import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccessModal } from '@/components/AccessModal';
import { SignInModal } from '@/components/SignInModal';

type AuthModalsContextType = {
  openSignIn: () => void;
  openAccess: () => void;
  closeSignIn: () => void;
  closeAccess: () => void;
};

const AuthModalsContext = createContext<AuthModalsContextType | undefined>(undefined);

export const useAuthModals = () => {
  const context = useContext(AuthModalsContext);
  if (!context) {
    throw new Error('useAuthModals must be used within AuthModalsProvider');
  }
  return context;
};

export const AuthModalsProvider = ({ children }: { children: ReactNode }) => {
  const [signInOpen, setSignInOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-open sign-in modal when redirected from Protected routes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('signin') === 'true') {
      setSignInOpen(true);
      params.delete('signin');
      const remaining = params.toString();
      navigate(location.pathname + (remaining ? `?${remaining}` : ''), { replace: true });
    }
  }, [location.search]);

  const openSignIn = () => setSignInOpen(true);
  const openAccess = () => setAccessOpen(true);
  const closeSignIn = () => setSignInOpen(false);
  const closeAccess = () => setAccessOpen(false);

  return (
    <AuthModalsContext.Provider value={{ openSignIn, openAccess, closeSignIn, closeAccess }}>
      {children}
      <SignInModal 
        open={signInOpen} 
        onOpenChange={setSignInOpen}
        onRequestAccess={() => {
          setSignInOpen(false);
          setAccessOpen(true);
        }}
      />
      <AccessModal 
        open={accessOpen} 
        onOpenChange={setAccessOpen}
      />
    </AuthModalsContext.Provider>
  );
};
